import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useLayoutEffect, useContext
} from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import MapaInfo from "./MapaInfo";
import { ThemeContext } from "../../../../Theme";
//import { google_api } from "../../../../config";
import LogoFarmageo from "../../../../assets/images/Logo-farmageo-borde.svg";
import { getAtributo } from "../../context/FuncionesContext";
import casaIcon from "../../../../assets/map/icons/casita.png";
import {
  calcZoom,
  ajustarZoom,
  getMaxDistanceBetweenPoints,
  obtenerUbicacion,
} from "../../../Pantalla/helper/funcionesMapa";

export function Mapa({ data, cab, display, requestNro }) {
  // 1) Carga de la API
  const { api_key_maps } = useContext(ThemeContext)
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: api_key_maps,
  });

  // 2) Refs para estado interno
  const [, forceUpdate] = useState(false);
  const maxDistance = useMemo(() => getMaxDistanceBetweenPoints(data), [data]);
  const [zoom, setZoom] = useState(16 - Math.log2(maxDistance.distanceKm));

  const showInfoWindowRef = useRef(false);
  const selectedElementRef = useRef(null);
  const activeMarkerPosRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const mapRef = useRef(null);
  const centerRef = useRef(null);

  // Limpiar timeout al desmontar
  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    },
    []
  );

  // 3) Calcular centro inicial y memoizar
  const latField = `${cab.id_a}_mapa_latitud`;
  const lngField = `${cab.id_a}_mapa_longitud`;

  const getInitialCenter = useCallback(async () => {
    const initMapaCenter = getAtributo("mapa_inicio", data[0], cab);
    const mapa_iniciar_en_cliente = getAtributo(
      "mapa_iniciar_en_cliente",
      data[0],
      cab
    );
    //console.log(initMapaCenter, requestNro);
    if (mapa_iniciar_en_cliente === "s") {
      const pos = await obtenerUbicacion();
      setUserPosition(pos);
      return pos;
    }
    if (initMapaCenter && requestNro === 0) {
      const [lat, lng] = initMapaCenter
        .split(",")
        .map((v) => parseFloat(v.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        // console.log("initCenter por atributo", initMapaCenter, requestNro);
        return { lat, lng };
      }
    }

    if (!data.length) {
      //console.log("initCenter default sin datos");
      return { lat: -32.95025459888096, lng: -60.66703610731241 };
    }

    const latValues = data
      .map((d) => Number(d[latField]))
      .filter((v) => !isNaN(v));

    const lngValues = data
      .map((d) => Number(d[lngField]))
      .filter((v) => !isNaN(v));

    const latm = latValues.reduce((s, d) => s + Number(d), 0) / data.length;
    const lngm = lngValues.reduce((s, d) => s + Number(d), 0) / data.length;
    //console.log("initCenter calculado", requestNro);
    return { lat: Number(latm), lng: Number(lngm) };
  }, [data, cab, latField, lngField]);

  const [initialCenter, setInitialCenter] = useState({
    lat: -32.95025459888096,
    lng: -60.66703610731241,
  });
  const [userPosition, setUserPosition] = useState(null);

  if (centerRef.current === null) centerRef.current = initialCenter;

  // const zoom = cab.mapa_zoom
  //   ? Number(cab.mapa_zoom)
  //   : cab.latitud_id_a && cab.longitud_id_a
  //   ? 8
  //   : 3;

  const mapaInfoConfig = cab.sc_hijos?.filter(
    (f) => f.componente === "mapa_info"
  );
  const hayMapaInfo = mapaInfoConfig?.length > 0;

  // 4) Handlers memorizados usando refs
  const handleMouseOver = useCallback(
    (element, e) => {
      if (!hayMapaInfo) return;
      const prev = selectedElementRef.current;
      if (prev?._key !== element._key) {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        selectedElementRef.current = element;
        if (e?.latLng) {
          activeMarkerPosRef.current = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
          };
        }
        showInfoWindowRef.current = true;
        forceUpdate((f) => !f);
        // center on marker without re-rendering map
        if (mapRef.current && activeMarkerPosRef.current) {
          mapRef.current.panTo(activeMarkerPosRef.current);
        }
      }
    },
    [hayMapaInfo]
  );

  const handleMouseOut = useCallback(() => {
    if (!hayMapaInfo) return;
    hoverTimeoutRef.current = setTimeout(() => {
      selectedElementRef.current = null;
      activeMarkerPosRef.current = null;
      showInfoWindowRef.current = false;
      forceUpdate((f) => !f);
    }, 300);
  }, [hayMapaInfo]);

  // 5) Icono memorizado
  const markerIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps) return undefined;
    return {
      url: LogoFarmageo,
      scaledSize: new window.google.maps.Size(25, 25),
      anchor: new window.google.maps.Point(0, 25),
    };
  }, [isLoaded]);

  // 6) Marcadores memorizados
  const memoizedMarkers = useMemo(
    () =>
      data.map((d) => {
        return (
          <Marker
            key={d._key}
            position={{ lat: Number(d[latField]), lng: Number(d[lngField]) }}
            icon={markerIcon}
            onMouseOver={(e) =>
              cab.mapa_OnMouseOver === "s" && handleMouseOver(d, e)
            }
            onMouseOut={() => cab.mapa_OnMouseOver === "s" && handleMouseOut()}
            onClick={(e) =>
              cab.mapa_OnMouseOver !== "s" && handleMouseOver(d, e)
            }
          />
        );
      }),
    [
      data,
      latField,
      lngField,
      cab.mapa_OnMouseOver,
      handleMouseOver,
      handleMouseOut,
      markerIcon,
    ]
  );

  useEffect(() => {
    getInitialCenter().then((center) => {
      setInitialCenter(center);
    });
  }, [getInitialCenter]);

  useLayoutEffect(() => {
    const rect = document.getElementById(cab.id_a).getBoundingClientRect();
    const z =
      cab.mapa_zoom && cab.mapa_zoom !== "" && !isNaN(parseFloat(cab.mapa_zoom))
        ? parseFloat(cab.mapa_zoom)
        : calcZoom(maxDistance.distanceKm) *
        ajustarZoom(rect.width, rect.height);
    setZoom(z);
  }, [cab.id_a, cab.mapa_zoom, maxDistance.distanceKm]);

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.panTo(initialCenter);
    }
  }, [data.length, initialCenter]);

  // 7) Renderización
  if (loadError) return <div>Error al cargar el mapa</div>;

  return (
    <div
      className="map_container"
      style={{ height: cab.height || "200px", display }}
      id={cab.id_a}
    >
      {!isLoaded ? (
        <div>Cargando mapa…</div>
      ) : (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: cab.height || "200px" }}
          zoom={zoom}
          center={centerRef.current}
          onLoad={(map) => {
            mapRef.current = map;
          }}
          onIdle={() => {
            centerRef.current = mapRef.current.getCenter()?.toJSON();
          }}
        >
          {userPosition ? (
            <Marker
              key={userPosition.lat + "-" + userPosition.lng}
              position={userPosition}
              icon={{
                url: casaIcon,
                scaledSize: new window.google.maps.Size(30, 30),
                anchor: new window.google.maps.Point(30, 30),
              }}
            />
          ) : (
            <></>
          )}
          {memoizedMarkers}
          {showInfoWindowRef.current && activeMarkerPosRef.current && (
            <InfoWindow
              position={activeMarkerPosRef.current}
              onCloseClick={handleMouseOut}
            >
              {selectedElementRef.current && (
                <MapaInfo
                  selectedElement={selectedElementRef.current}
                  mapaInfo={mapaInfoConfig[0]}
                  cab={cab}
                  data={data}
                />
              )}
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </div>
  );
}

export default Mapa;
